import { appThemeColors } from "styles/appTheme";
import { appThemeFonts, appTypography } from "styles/appThemeFonts";
import {
  appThemeRadius,
  appThemeSize,
  appThemeSpace,
  appThemeTransition,
} from "styles/appThemeTokens";

export const DRAWER_WIDTH = appThemeSize.drawerWidth;
export const DRAWER_COLLAPSED_WIDTH = appThemeSize.drawerCollapsedWidth;
export const SIDE_NAV_ITEM_HEIGHT = appThemeSize.navItemHeight;
export const SIDE_NAV_ICON_SIZE = appThemeSize.navIcon;

export const sideNavTypography = {
  ...appTypography.ui,
};

export const sideNavItemStyles = {
  position: "relative",
  display: "flex",
  alignItems: "center",
  gap: appThemeSpace.xl,
  width: "100%",
  justifyContent: "flex-start",
  textAlign: "left",
  border: 0,
  background: "none",
  cursor: "pointer",
  padding: `0px 11px`,
  height: "var(--wm-nav-h, 40px)",
  borderRadius: appThemeRadius.md,
  color: appThemeColors.text.primary,
  ...sideNavTypography,
  "&:hover": {
    backgroundColor: appThemeColors.background.kbd,
  },
};

export const sideNavItemSelectedStyles = {
  backgroundColor: appThemeColors.background.selected,
  color: appThemeColors.text.heading,
  fontWeight: appThemeFonts.weight.semibold,
  "&:hover": {
    backgroundColor: appThemeColors.background.selected,
  },
};

export const sideNavSelectedIndicatorStyles = {
  position: "absolute",
  left: 0,
  top: appThemeSpace.md,
  bottom: appThemeSpace.md,
  width: 3,
  borderRadius: appThemeRadius.indicator,
  backgroundColor: appThemeColors.blue.light,
};

export const getNavSx = ({ currentDrawerWidth, isCollapsed }) => ({
  width: currentDrawerWidth,
  flex: "0 0 auto",
  display: "flex",
  flexDirection: "column",
  boxSizing: "border-box",
  backgroundColor: appThemeColors.background.paper,
  color: appThemeColors.text.primary,
  borderRight: `1px solid ${appThemeColors.border.default}`,
  "--wm-nav-h": SIDE_NAV_ITEM_HEIGHT,
  transition: appThemeTransition.navWidth,
  overflow: "hidden",
  height: "100%",
  ...sideNavTypography,
  "& .MuiListItemButton-root:not(.side-nav-sub-item)": {
    ...sideNavItemStyles,
    ...(isCollapsed && {
      justifyContent: "center",
      padding: "0px",
      gap: 0,
    }),
  },
  "& .MuiListItemButton-root.Mui-selected:not(.side-nav-sub-item)": {
    ...sideNavItemSelectedStyles,
  },
  "& .MuiListItemButton-root.Mui-selected:not(.side-nav-sub-item) .MuiListItemIcon-root": {
    color: appThemeColors.blue.dark,
  },
  "& .MuiListItemButton-root.Mui-selected:not(.side-nav-sub-item) .MuiListItemText-primary": {
    fontWeight: appThemeFonts.weight.semibold,
    color: "inherit",
  },
  "& .MuiListItemIcon-root": {
    color: appThemeColors.text.muted,
    minWidth: "auto",
    "& .MuiSvgIcon-root": {
      width: SIDE_NAV_ICON_SIZE,
      height: SIDE_NAV_ICON_SIZE,
      fontSize: SIDE_NAV_ICON_SIZE,
    },
  },
  "& .MuiListItemText-root": {
    margin: 0,
    whiteSpace: "nowrap",
    overflow: "hidden",
  },
  "& .MuiListItemText-primary": {
    ...sideNavTypography,
    color: "inherit",
  },
});

export const navScrollSx = {
  flex: 1,
  overflowY: "auto",
  overflowX: "hidden",
  padding: `${appThemeSpace.lg} ${appThemeSpace.lg} ${appThemeSpace.xl}`,
};

export const navListSx = {
  padding: 0,
};

export const navItemWrapSx = {
  display: "block",
  marginBottom: "1px",
};

export const subMenuListSx = {
  margin: `${appThemeSpace.xxs} 0 ${appThemeSpace.sm} 34px`,
  padding: 0,
  display: "flex",
  flexDirection: "column",
  borderLeft: `1px solid ${appThemeColors.border.default}`,
};

export const getSubMenuItemButtonSx = (isActive) => ({
  textAlign: "left",
  border: 0,
  background: "none",
  cursor: "pointer",
  padding: `${appThemeSpace.sm} ${appThemeSpace.lg} !important`,
  marginLeft: "-1px",
  minHeight: "auto",
  height: "auto",
  borderLeft: `2px solid ${isActive ? appThemeColors.blue.light : "transparent"}`,
  borderRadius: appThemeRadius.subItem,
  ...appTypography.uiSub,
  color: isActive ? appThemeColors.text.heading : appThemeColors.text.primary,
  fontWeight: isActive
    ? appThemeFonts.weight.semibold
    : appThemeFonts.weight.regular,
  "& .MuiListItemText-root": {
    margin: 0,
  },
  "& .MuiListItemText-primary": {
    ...appTypography.uiSub,
    color: "inherit",
    fontWeight: "inherit",
  },
  "&.Mui-selected": {
    backgroundColor: "transparent !important",
    "&:hover": {
      backgroundColor: `${appThemeColors.background.kbd} !important`,
    },
  },
  "&:hover": {
    backgroundColor: appThemeColors.background.kbd,
  },
});
export const sideNavReturnBarSx = {
  flexShrink: 0,
  padding: "18px 10px 8px",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
};

export const sideNavReturnButtonSx = {
  textTransform: "none",
  justifyContent: "center",
  color: appThemeColors.text.lightBlue,
  fontWeight: appThemeFonts.weight.medium,
  ...appTypography.ui,
  "& .MuiButton-endIcon": {
    marginLeft: appThemeSpace.sm,
    marginRight: 0,
    color: appThemeColors.text.lightBlue,
  },
  "& .MuiButton-endIcon .MuiSvgIcon-root": {
    fontSize: appThemeSize.navIcon,
  },
  "&:hover": {
    backgroundColor: appThemeColors.background.kbd,
  },
};

/** Azuria lockup at the foot of the expanded nav (matches shell §4.1). */
export const sideNavLogoFooterSx = {
  flex: "0 0 auto",
  padding: "14px 16px 18px",
  display: "flex",
  justifyContent: "center",
  borderTop: `1px solid ${appThemeColors.background.rule}`,
};

export const sideNavLogoImgSx = {
  width: "100%",
  maxWidth: 125,
  height: "auto",
  opacity: 0.92,
  display: "block",
};
